import { useState } from "react";
import { VoteAnswer } from "../../api/events-api";
import { VoteAuditWithEditor } from "../../api/vote-audit-api";
import { LogeMember } from "../../types/entities";
import { Icon } from "../dss/Icon";
import { Sheet } from "../dss/Sheet";

/** An answer, or nothing at all — a member who hasn't said either way. */
export type Answer = VoteAnswer | undefined;

/**
 * The four states a member can be in, and the one place their colours,
 * wording and ordering are decided. Every surface on the details page — the
 * tally, the meter, the roster chips, the avatar tints — reads from here, so
 * a member's colour is the same in all of them.
 */
export const TONES = {
  yes: {
    label: "Deltager",
    tint: "bg-vote-yes-soft",
    chip: "bg-vote-yes-soft text-vote-yes-ink",
    bar: "bg-vote-yes",
  },
  maybe: {
    label: "Måske",
    tint: "bg-vote-maybe-soft",
    chip: "bg-vote-maybe-soft text-vote-maybe-ink",
    bar: "bg-vote-maybe",
  },
  no: {
    label: "Afbud",
    tint: "bg-clay-soft",
    chip: "bg-clay-soft text-clay",
    bar: "bg-clay",
  },
  none: {
    label: "Afventer",
    tint: "bg-paper2",
    chip: "bg-paper2 text-ink3",
    bar: "bg-line",
  },
} as const;

export type ToneKey = keyof typeof TONES;

export const toneKeyFor = (answer: Answer): ToneKey =>
  answer === VoteAnswer.YES
    ? "yes"
    : answer === VoteAnswer.MAYBE
      ? "maybe"
      : answer === VoteAnswer.NO
        ? "no"
        : "none";

export interface Tally {
  yes: number;
  maybe: number;
  no: number;
  none: number;
}

/**
 * The headline numbers: how the loge is split across the answers actually in
 * use. A column standing at nought — "0 Afbud" on a samling nobody has
 * declined — is a fact so unremarkable it's not worth a fifth of the row;
 * dropping it lets the ones that do say something read larger and quieter
 * at once.
 */
export function AttendanceTally({ tally }: { tally: Tally }) {
  // Only the figures are public here. Who exactly is still missing is a
  // different question, and the roster below answers it for the people it
  // concerns rather than for everyone browsing the samling.
  const allCells: Array<{ key: ToneKey; value: number }> = [
    { key: "yes", value: tally.yes },
    { key: "maybe", value: tally.maybe },
    { key: "no", value: tally.no },
    { key: "none", value: tally.none },
  ];
  const cells = allCells.filter(({ value }) => value > 0);
  const labels: Record<ToneKey, string> = {
    yes: "Deltager",
    maybe: "Måske",
    no: "Afbud",
    none: "Mangler",
  };

  // Nothing to report yet — no members, or a brand-new samling with not
  // even a "mangler" count to show (both editableMembers and the host are
  // empty). Leaves the meter below to stand on its own rather than an empty
  // strip of grid.
  if (cells.length === 0) return null;

  return (
    // No card, no border, and the row itself is never stretched or centred
    // — it's only ever as wide as its cells need, sitting at the left edge
    // whether that's one cell or four. Each cell is a fixed width, though,
    // rather than sized to its own label: letting "Deltager" and "Afbud"
    // claim different amounts of room made the row look ragged instead of
    // like a set of figures that belong together. Fixed-width columns read
    // as one thing; free-width ones read as leftover clutter.
    <div className="flex flex-wrap gap-x-3 gap-y-2">
      {cells.map(({ key, value }) => (
        <div key={key} data-testid={`tally-${key}`} className="w-14 flex-shrink-0 text-center">
          <div className="flex items-center justify-center gap-1.5">
            <span className={`w-[5px] h-[5px] rounded-full flex-shrink-0 ${TONES[key].bar}`} />
            <span
              data-tally-value
              className="font-display font-bold text-[15px] leading-none tracking-tight tabular-nums text-ink"
            >
              {value}
            </span>
          </div>
          <div className="font-mono text-[7.5px] tracking-[1px] text-ink3 uppercase mt-1">
            {labels[key]}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * The same split again as one bar — the shape of the turnout read at a
 * glance, before any of the numbers are.
 *
 * The track is a fixed-height box and the segments are percentage widths
 * inside it, so a changing answer slides the boundaries rather than
 * relaying anything out. Members who haven't answered are simply the part
 * of the track left uncovered.
 */
export function AttendanceMeter({ tally }: { tally: Tally }) {
  const total = tally.yes + tally.maybe + tally.no + tally.none;
  const pct = (n: number) => (total > 0 ? (n / total) * 100 : 0);
  const segments: ToneKey[] = ["yes", "maybe", "no"];

  return (
    <div className="mt-3 h-1.5 rounded-full bg-paper2 overflow-hidden flex" aria-hidden>
      {segments.map((key) => (
        <div
          key={key}
          className={`${TONES[key].bar} transition-[width] duration-500 ease-out`}
          style={{ width: `${pct(tally[key])}%` }}
        />
      ))}
    </div>
  );
}

export interface RosterEntry {
  member: LogeMember;
  /**
   * The host's default — attending, since nobody asks them to vote on their
   * own samling — is already folded in here by the caller: this is never
   * "no answer" for a host who hasn't been given an explicit one, only for
   * an ordinary member who genuinely hasn't said. An admin can still record
   * a real answer for the host, and it arrives here the same way.
   */
  answer: Answer;
  /** Hosting it. Doesn't change how their row is coloured — see `answer`. */
  isHost: boolean;
  /** The person reading the page. */
  isViewer: boolean;
  /**
   * Every recorded change to this member's answer on the confirmed date,
   * newest first. Empty or absent for the great majority, who answered once
   * and left it there.
   */
  auditTrail?: VoteAuditWithEditor[];
}

/** Wall-clock Copenhagen, which is the only clock the loge keeps. */
const COPENHAGEN = new Intl.DateTimeFormat("da-DK", {
  timeZone: "Europe/Copenhagen",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

/** The word the roster would use for a recorded answer, including none. */
const answerLabel = (value: number | null | undefined) =>
  TONES[toneKeyFor(value === null || value === undefined ? undefined : (value as VoteAnswer))]
    .label;

/**
 * What this member had said when the trail starts — the answer the first
 * recorded change moved them away from.
 *
 * `undefined` when there is nothing to compare against: either nothing was
 * ever changed, or the oldest row predates the trigger that records what an
 * answer was changed *from*. An unknown starting point is treated as a real
 * difference rather than assumed away, so the flag errs towards telling the
 * vært something they can check.
 */
const startingAnswer = (trail: VoteAuditWithEditor[]) =>
  trail.length > 0 ? trail[trail.length - 1].old_answer : undefined;

/**
 * Whether this member's answer is somewhere other than where it started.
 *
 * A round trip is not a change. Someone who was down as afbud, said måske for
 * an afternoon and went back to afbud has left the vært's count exactly as
 * they found it, and a standing warning about it is just noise — which is the
 * same reason a yes that wandered off and came back doesn't raise one either.
 * Only the ones that cost a seat are flagged at all: firming up to "deltager"
 * is news the vært doesn't need warning about.
 */
const hasDrifted = (answer: Answer, trail: VoteAuditWithEditor[]) => {
  if (trail.length === 0) return false;
  if (answer !== VoteAnswer.MAYBE && answer !== VoteAnswer.NO) return false;
  const started = startingAnswer(trail);
  return started === null || started === undefined || started !== answer;
};

/** One member's history on the confirmed date, as the sheet needs it. */
interface AuditDetail {
  member: LogeMember;
  /** Newest first. */
  trail: VoteAuditWithEditor[];
}

function AuditSheetBody({ member, trail }: AuditDetail) {
  return (
    <>
      <div className="font-display text-[14px] font-bold uppercase tracking-tight text-ink2 -mt-2 mb-1">
        {member.emoji} {member.name}
      </div>
      <div className="font-mono text-[9px] tracking-[1.4px] uppercase text-ink3 mb-3.5">
        {trail.length} {trail.length === 1 ? "ændring" : "ændringer"} · seneste først
      </div>

      <div className="flex flex-col gap-2">
        {trail.map((entry) => {
          // Someone other than the member themselves recorded it. A member's
          // own change is between them and the roster; naming them on the row
          // would only be repeating the name at the top of the sheet.
          const byAdmin = !!(entry.edited_by && entry.edited_by !== member.id);
          return (
            <div key={entry.id} className="bg-paper2 rounded-lg px-4 py-3">
              <div className="font-mono text-[11.5px] tracking-[0.5px] text-ink2">
                {COPENHAGEN.format(new Date(entry.changed_at))}
              </div>
              <div className="flex items-center gap-2 mt-1.5 font-display text-[13px] font-bold uppercase tracking-tight">
                <span className="text-ink3">{answerLabel(entry.old_answer)}</span>
                <Icon name="arrow-right" size={10} className="text-ink3" />
                <span>{answerLabel(entry.new_answer)}</span>
              </div>
              {byAdmin && entry.editor && (
                <div className="flex items-center gap-1.5 mt-2 font-mono text-[9px] tracking-[1.2px] uppercase text-ink3">
                  <span className="text-[11px] leading-none">{entry.editor.emoji}</span>
                  <span>{entry.editor.name} · administrator</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-4 text-[13px] leading-relaxed text-ink3">
        Tilmeldingen står et andet sted end da samlingen blev bekræftet.
      </div>
    </>
  );
}

/**
 * Everyone, one row each, in a fixed order that an answer never changes.
 *
 * The grouped avatar grids this replaced re-sorted themselves on every
 * answer: a tap moved your own face from one group to another, resized both,
 * and dragged the rest of the page along behind them. Here a member keeps
 * their row and only the chip at the end of it changes colour — so the page
 * is still under the finger that just tapped it.
 */
export function Roster({ entries }: { entries: RosterEntry[] }) {
  // One sheet for the whole list rather than one parked under every row.
  // `detail` deliberately outlives the close: the sheet is still on screen
  // for the length of its exit, and still needs something to draw.
  const [detail, setDetail] = useState<AuditDetail | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  return (
    <>
      <div data-roster className="bg-card border border-line-soft rounded-lg overflow-hidden">
        {entries.map(({ member, answer, isHost, isViewer, auditTrail }) => {
          const tone = TONES[toneKeyFor(answer)];
          const trail = auditTrail ?? [];
          const showAlert = hasDrifted(answer, trail);

          return (
            <div
              key={member.id}
              className="flex items-center gap-3 px-3 h-[52px] border-t border-line-soft first:border-t-0"
            >
              <div
                className={`w-9 h-9 rounded-full flex items-center justify-center text-lg flex-shrink-0 transition-colors duration-300 ${tone.tint}`}
              >
                {member.emoji}
              </div>
              <div
                data-roster-name
                className="flex-1 min-w-0 font-display text-[14px] font-bold uppercase tracking-tight truncate"
              >
                {member.name}
              </div>
              {/* One tag at most: hosting it says more than reading it does. */}
              {(isHost || isViewer) && (
                <span className="font-mono text-[8.5px] tracking-[1.2px] uppercase text-ink3 border border-line-soft rounded px-1.5 py-0.5 flex-shrink-0">
                  {isHost ? "Vært" : "Dig"}
                </span>
              )}
              {/* Full-strength clay, not the tint: this is the one mark on the
                  page that is supposed to catch an eye scanning past it. The
                  negative margin buys a finger-sized target back without
                  letting the icon push the row's other contents around. */}
              {showAlert && (
                <button
                  onClick={() => {
                    setDetail({ member, trail });
                    setSheetOpen(true);
                  }}
                  className="-m-1.5 p-1.5 text-clay active:opacity-60 transition-opacity flex-shrink-0"
                  aria-label={`Ændret efter bekræftelse — ${member.name}`}
                >
                  <Icon name="triangle-exclamation" size={15} />
                </button>
              )}
              {/* Fixed width, so the chip's own edge doesn't travel when
                  "Deltager" becomes "Afbud" — the row has to hold completely
                  still under a change of answer, not merely keep its height. */}
              <span
                data-roster-answer
                className={`w-[76px] flex-shrink-0 text-center font-mono text-[9px] tracking-[1.1px] uppercase rounded py-1.5 transition-colors duration-300 ${tone.chip}`}
              >
                {tone.label}
              </span>
            </div>
          );
        })}
      </div>

      <Sheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        title={detail ? "Ændret efter bekræftelse" : undefined}
      >
        {detail && (
          <>
            <AuditSheetBody {...detail} />
            <button
              onClick={() => setSheetOpen(false)}
              className="w-full mt-5 py-3.5 px-4 rounded-lg bg-ink text-paper font-display text-sm font-bold tracking-wide uppercase"
            >
              Luk
            </button>
          </>
        )}
      </Sheet>
    </>
  );
}
